import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Room } from 'src/sync/room';
import { Account } from 'src/sync/account';
import { Kb } from 'src/util/kb';
import { WatchAwareComponent } from '../watch-aware.component';
import { C, IAttendee, IMessage } from 'src/sync/_api';
import { RollBase } from 'dice-roller-parser';
import { Dictionary } from 'src/extensions';
import { Store } from 'src/sync/store';
import { Util } from 'src/util/util';

interface IChatMessage {
    id: number;
    name: string;
    color: string;
    date: string;
    text: string;
    channel: string;
    receiverId: string;
    imageUrl?: string;
    rolls?: RollBase[] | any;
}

@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
    standalone: false
})
export class ChatComponent extends WatchAwareComponent {
    @Input() unread = 0;
    @Output() unreadChange = new EventEmitter<number>();

    @ViewChild('chatbox') chatboxElement!: ElementRef<HTMLElement>;
    @ViewChild('chatinput') inputElement!: ElementRef<HTMLInputElement>;

    public get width() { return Store.isMobile.value ? '100vw' : this.user.chat.width + 'px' }
    public user = Account.settings.get();
    public attendance: Dictionary<IAttendee> = {}
    public rollHistory: IMessage[] = [];
    public updownIndex: number = -1;
    public filteredMessages: IChatMessage[] = [];
    public message: string = '';
    public showSettings = false;
    public get chatmode() { return Store.chatmode }
    public enterSubscription = Kb.on({ code: 'Affirmative' }, () => this.inputElement.nativeElement.focus());
    public escapeSubscription = Kb.on({ code: 'Escape' }, () => this.close());

    public ngAfterViewInit() {
        this.trackMany([
            Account.settings.watch((x) => {
                this.user = x;
                this.changeDetectorRef.detectChanges();
            }),
            Room.attendance.watchAll(() => {
                const old = Util.clone(this.attendance);
                this.attendance = Room.attendance.toJSON();
                this.checkForUpdate(old, this.attendance);
            }),
            Room.chat.watch((history) => this.update(history))
        ]);

        /* Don't propagate user typing up to document listeners */
        this.inputElement.nativeElement.addEventListener('keydown', (e) => e.stopPropagation());

        document.querySelector('#chat-menu')?.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
        });

        /* Re-enable context menu for the chat */
        this.chatboxElement.nativeElement.addEventListener('contextmenu', event => event.stopPropagation());
    }

    private checkForUpdate(old: Dictionary<IAttendee>, current: Dictionary<IAttendee>) {
        for (let id in old) {
            const nameChange = old[id].name != current[id]?.name;
            const colorChange = old[id].color != current[id]?.color;
            if (nameChange || colorChange) {
                this.update(Room.chat.toArray(), false);
                break;
            }
        }
    }

    // TODO: Implement smarter system for pulling new chat messages
    public update(history: IMessage[], scroll = true) {
        const chatbox = document.querySelector('#chatbox') as HTMLElement;
        const scrollAnyway = chatbox.scrollTop + chatbox.clientHeight == chatbox.scrollHeight;

        let filtered = history.reverse().splice(Math.max(history.length - 500, 0))
            .filter(x => new Date(x.date).getTime() > this.user.clearDate)
            .filter(x => x.channel != C.Ref || Store.isReferee.value)
            .filter(x => {
                const isNotPrivate = x.channel != C.Whisper && x.channel != C.AI;
                const isTarget = x.receiverId == Account.id;
                const isSender = x.accountId == Account.id;
                return isNotPrivate || isTarget || isSender;
            });

        const messages: IChatMessage[] = [];
        for (let message of filtered) {
            // TODO: Referee messages should be filtered earlier, but this is a hack to run ref commands on clients
            if (message.channel == C.Ref) this.tryPlayMusic(message);
            if (message.channel == C.Ref && Store.isReferee.value != true) continue;

            const user = this.attendance[message.accountId];

            let sender;
            if (message.accountId === C.AI) {
                sender = { name: 'AI', color: 'orangered' };
            } else if (user) {
                sender = Util.clone(user);
            } else {
                sender = { name: 'Deleted User', color: 'red', referee: false };
            }

            if (message.channel == C.Whisper) {
                sender.color = '#ff8dff';
                if (message.accountId == Account.id) {
                    const receiver = this.attendance[message.receiverId].name;
                    sender.name = `To [${receiver}]:`;
                } else {
                    sender.name = `[${sender.name}] whispers:`;
                }
            }

            messages.push({
                id: message.date,
                name: sender?.name ?? 'Anon',
                color: sender?.color,
                date: new Date(message.date).toLocaleTimeString(),
                text: message.text,
                channel: message.channel,
                imageUrl: this.getImageUrl(message.text),
                rolls: message.rolls,
                receiverId: message.receiverId
            });
        }

        if (this.filteredMessages.length == 0 || (this.filteredMessages.length > 0 && messages.length == 0)) {
            this.unread = 0;
        } else {
            const isForeignSender = filtered.slice(-1)[0]?.accountId !== Account.id;
            if (isForeignSender) this.unread++;
        }

        this.filteredMessages = messages;
        const rollHistory = filtered.filter(x => x.accountId == Account.id && x.rolls?.length > 0).reverse();
        this.rollHistory = Util.uniqueBy(rollHistory, (x: IMessage) => x.text);

        this.unreadChange.emit(this.unread);
        this.changeDetectorRef.detectChanges();

        if (scroll) this.scrollChatbox(scrollAnyway);
    }

    // TODO: Replace this ghetto music implementation
    private tryPlayMusic(message: IMessage) {
        const exists = this.filteredMessages.find(x => x.id == message.date);
        const twoSecondsAgo = (new Date().getTime()) - 2000;
        const isRecent = (new Date(message.date).getTime()) > twoSecondsAgo;
        if (exists == undefined && isRecent && message.channel == C.Ref) {
            if (message.text == 'stop' || message.text == 'music1' || message.text == 'music2' || message.text == 'evil') {
                Store.playMusic(message.text);
            }

            if (message.text == 'victory') {
                const element = document.getElementById('emote-overlay') as HTMLElement;
                element.style.animation = 'fade 3.5s linear'
                Store.victory.play();
                setTimeout(() => element.style.animation = '', 3500);
            }
        }
    }

    // TODO: Refactor this unruly mess.
    public shout() {
        if (this.message) {
            const clearMessage = '/clear';
            const unclearMessage = '/unclear';

            if (this.message.startsWith(clearMessage)) {
                Account.settings.modify((x) => x.clearDate = Date.now() - 1);
                this.update(Room.chat.toArray(), false);
            } else if (this.message.startsWith(unclearMessage)) {
                Account.settings.modify((x) => x.clearDate = 0);
                this.update(Room.chat.toArray(), false);
            } else if (this.message.startsWith('/w') || this.message.startsWith('/whisper')) {
                this.whisper();
            } else {
                Room.shout(this.message, Store.chatmode);
                if (Store.chatmode == C.AI && Account.HasAiApiKey()) {
                    Account.completion(this.message).then((response) => {
                        Room.shout(response, C.AI, Account.id, C.AI);
                        this.changeDetectorRef.detectChanges();
                    });
                }
            }

            this.message = '';
            this.updownIndex = -1;
        };

        if (this.user.chat.alwaysOpen == false && this.user.chat.keepOpenAfterSend == false) {
            this.close();
        }
    }

    private whisper() {
        const names = Object.values(this.attendance).map(x => x.name);
        const escapeSpecialCharacters = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const namesRegex = names.map(escapeSpecialCharacters).join("|");
        const pattern = new RegExp(`^\\s*\\/(?:w|whisper)\\s+(${namesRegex})\\s+(.*)$`, "i");

        const match = this.message.match(pattern);
        if (match) {
            const target = Room.getAttendee(match[1]);
            if (target!.id != Account.id) {
                Room.shout(match[2], C.Whisper, target!.id);
            }
        }
    }

    public next() {
        if (this.rollHistory.length == 0) return;
        if (this.updownIndex < (this.rollHistory.length - 1)) {
            this.updownIndex++;
        }

        this.setMessage(this.rollHistory[this.updownIndex].text);
    }

    public previous() {
        if (this.rollHistory.length == 0) return;
        if (this.updownIndex > -1) {
            this.updownIndex--;
            if (this.updownIndex == -1) {
                this.message = '';
            } else {
                this.setMessage(this.rollHistory[this.updownIndex].text);
            }
        }
    }

    public trackByDate(index: number, item: IChatMessage): number {
        return item.id;
    }

    private setMessage(message: string) {
        const input = this.inputElement.nativeElement;
        document.getElementById('chat-destination')?.focus();
        this.message = message;
        // Do it once as fast as possible to avoid jittering, do it again slow for Firefox users.
        setTimeout(() => {
            input.selectionStart = input.selectionEnd = input.value.length;
            input.focus();
        });
        setTimeout(() => {
            input.selectionStart = input.selectionEnd = input.value.length;
            input.focus();
        }, 10);
    }

    // TODO: UX Work is needed for this feature to make sense
    public setWhisperTarget(message: IChatMessage) {
        // TODO: Rewrite to figure out who needs to be messaged

        // const target = this.attendance[targetId].name;
        // this.setMessage(`/w ${target} ${this.message}`);
    }

    public keydown(event: Event): void {
        const e = event as KeyboardEvent;
        if (e.key === 'Enter') return this.shout();
        if (e.key === 'Escape') return this.close();
        if (e.key === 'ArrowDown') return this.previous();
        if (e.key === 'ArrowUp') return this.next();
        if (e.key === 'Tab') {
            e.preventDefault(); // stop focus change
            return this.mode(e);
        }
    }

    public mode(e?: Event): void {
        const destination = document.querySelector('#chat-destination') as Element;
        const modes = Object.values(C).filter((mode) => mode !== C.AI || Account.HasAiApiKey()).filter(mode => mode != C.Whisper);
        const currentIndex = modes.indexOf(Store.chatmode as any);
        const safeIndex = currentIndex === -1 ? 0 : currentIndex;
        const backwards = (e as KeyboardEvent).shiftKey;
        const nextIndex = backwards
            ? (safeIndex - 1 + modes.length) % modes.length
            : (safeIndex + 1) % modes.length;

        Store.chatmode = modes[nextIndex] as C;
        destination.innerHTML = `[${Store.chatmode}]`;

        e?.preventDefault();
        e?.stopPropagation();
    }

    public scrollChatbox(forceScroll = false) {
        if (forceScroll || this.user.chat.autoscroll == true) {
            this.chatboxElement.nativeElement.scrollTop = this.chatboxElement.nativeElement.scrollHeight;
        }
    }

    public changeSetting(event: Event) {
        Account.settings.modify((x) => x.chat = this.user.chat);
        this.scrollChatbox();
        event.stopPropagation();
    }

    public openSettings(show: boolean) {
        this.showSettings = show;
        this.scrollChatbox();
    }

    public close() {
        this.message = '';
        this.updownIndex = -1;
        this.inputElement.nativeElement.blur();
        this.openSettings(false);
    }

    public static getColor(op: string) {
        if (op == '+') {
            return 'limegreen';
        } else if (op == '-') {
            return 'orangered';
        } else {
            return 'skyblue';
        }
    }

    private getImageUrl(message: string) {
        try {
            const url = new URL(message);
            const actual = url.origin + url.pathname;
            const isImageUrl = (/\.(gif|jpg|jpeg|tiff|png)$/i).test(actual);
            if (isImageUrl) {
                return url.href;
            }
            const twitter = /(http?s:\/\/pbs.twimg.com.*)\?format=(.*)&.*/.exec(url.href);
            if (twitter) {
                return `${twitter[1]}.${twitter[2]}`
            }
            const imgur = /(http?s:\/\/imgur.com\/)(.*)/.exec(url.href);
            if (imgur) {
                return `https://i.imgur.com/${imgur[2]}.png`;
            }
            return undefined;
        } catch {
            return undefined;
        }
    }

    public override ngOnDestroy() {
        super.ngOnDestroy();
        this.enterSubscription.unsubscribe();
        this.escapeSubscription.unsubscribe();
    }
}
